// YouTube IFrame API supported playback rates
export const YOUTUBE_RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

/**
 * Snaps an arbitrary playback rate to the nearest rate supported by YouTube.
 */
export function snapToYouTubeRate(rate: number): number {
  return YOUTUBE_RATES.reduce((prev, curr) =>
    Math.abs(curr - rate) < Math.abs(prev - rate) ? curr : prev
  );
}

/**
 * Given a base BPM and the current BPM, returns the next BPM that maps to a
 * higher YouTube-supported playback rate than the current one. Used by the
 * auto-speed-up feature so each step lands on a rate YouTube can actually play.
 *
 * - `desiredBpm` is the caller's preferred next BPM (e.g. current + increment);
 *   if it already requires a higher supported rate than the current one, the
 *   result honours it (snapped up); otherwise we jump to the next rate above.
 * - Returns `null` when the current rate is already at the YouTube maximum.
 */
export function nextYouTubeBpm(
  currentBpm: number,
  desiredBpm: number,
  baseBpm: number,
): number | null {
  if (baseBpm <= 0) return null;
  const currentRate = snapToYouTubeRate(currentBpm / baseBpm);
  const desiredRate = desiredBpm / baseBpm;
  // Find the smallest supported rate that is both >= desiredRate and > currentRate.
  const next = YOUTUBE_RATES.find((r) => r > currentRate && r >= desiredRate)
    ?? YOUTUBE_RATES.find((r) => r > currentRate);
  if (next === undefined) return null;
  return Math.round(next * baseBpm);
}


export class YouTubeEngine {
  private player: YT.Player | null = null;
  private loopInterval: ReturnType<typeof setInterval> | null = null;
  private loopStart: number = 0;
  private loopEnd: number = 0;
  private onLoopComplete: (() => void) | null = null;
  private lastLoopCompleteTime: number = 0;
  private snappedRate: number = 1;
  /** When true, the engine is in a logical "stopped" state but the underlying
   *  YouTube player keeps playing silently to avoid showing YouTube's pause
   *  poster (the centred play arrow). */
  private silent: boolean = false;

  /**
   * Attach the underlying YT.Player instance (called from the onReady callback).
   */
  attachPlayer(player: YT.Player): void {
    this.player = player;
  }

  /**
   * Configure the playback loop. The engine will automatically seek back to
   * loopStart whenever the current time passes loopEnd, and fire onComplete
   * once per loop iteration.
   */
  setLoop(startTime: number, endTime: number, onComplete?: () => void): void {
    this.loopStart = startTime;
    this.loopEnd = endTime;
    this.onLoopComplete = onComplete || null;
    this.lastLoopCompleteTime = 0;

    // Restart loop monitor
    this.stopLoopMonitor();
    this.startLoopMonitor();
  }

  /**
   * Set playback rate based on current BPM vs. original BPM.
   * Rate is snapped to the nearest value supported by YouTube.
   * Returns the actual rate applied.
   *
   * NOTE: YouTube does not support independent pitch compensation —
   * the audio pitch will shift with speed, unlike the Tone.js engine.
   */
  setPlaybackRate(bpm: number, originalBpm: number): number {
    if (!this.player) return 1;
    const desiredRate = bpm / originalBpm;
    this.snappedRate = snapToYouTubeRate(desiredRate);
    this.player.setPlaybackRate(this.snappedRate);
    return this.snappedRate;
  }

  /**
   * Start playback from the given start time (or the current loop start).
   */
  play(startTime?: number): void {
    if (!this.player) return;
    const seekTo = startTime !== undefined ? startTime : this.loopStart;
    this.silent = false;
    try { this.player.unMute(); } catch { /* ignore */ }
    try { this.player.setPlaybackRate(this.snappedRate); } catch { /* ignore */ }
    try { (this.player as any).setPlaybackQuality?.('highres'); } catch { /* ignore */ }
    this.player.seekTo(seekTo, true);
    this.player.playVideo();
    this.startLoopMonitor();
  }

  /**
   * Pause playback and stop the loop monitor.
   *
   * To avoid YouTube's pause-state poster (the big centred play arrow), we
   * keep the underlying player technically "playing" but muted at the slowest
   * supported rate.
   */
  pause(): void {
    if (!this.player) return;
    this.silent = true;
    this.stopLoopMonitor();
    try { this.player.mute(); } catch { /* ignore */ }
    try { this.player.setPlaybackRate(YOUTUBE_RATES[0]); } catch { /* ignore */ }
    try { (this.player as any).setPlaybackQuality?.('small'); } catch { /* ignore */ }
    // Intentionally do NOT call pauseVideo() — that would show the poster.
  }

  /**
   * Stop playback and seek back to loop start.
   *
   * Like `pause()`, this keeps the player silently running so the YouTube
   * pause poster never appears. The video is muted, slowed to the minimum
   * supported rate, and seeked back to the loop start.
   */
  stop(): void {
    if (!this.player) return;
    this.silent = true;
    this.stopLoopMonitor();
    try { this.player.mute(); } catch { /* ignore */ }
    try { this.player.setPlaybackRate(YOUTUBE_RATES[0]); } catch { /* ignore */ }
    try { (this.player as any).setPlaybackQuality?.('small'); } catch { /* ignore */ }
    try { this.player.seekTo(this.loopStart, true); } catch { /* ignore */ }
  }

  isReady(): boolean {
    return this.player !== null;
  }

  /** Duration of the loaded video in seconds (0 if unknown). */
  getDuration(): number {
    if (!this.player) return 0;
    try {
      const d = this.player.getDuration();
      return typeof d === 'number' && isFinite(d) ? d : 0;
    } catch {
      return 0;
    }
  }

  isPlaying(): boolean {
    if (!this.player) return false;
    if (this.silent) return false;
    return this.player.getPlayerState() === YT.PlayerState.PLAYING;
  }

  dispose(): void {
    this.stopLoopMonitor();
    this.player = null;
  }

  // ── private ──────────────────────────────────────────────────────────────

  private startLoopMonitor(): void {
    if (this.loopInterval) return; // already running

    this.loopInterval = setInterval(() => {
      if (!this.player) return;
      if (this.player.getPlayerState() !== YT.PlayerState.PLAYING) return;

      const currentTime = this.player.getCurrentTime();

      if (currentTime >= this.loopEnd) {
        // Loop: seek back to the start
        this.player.seekTo(this.loopStart, true);

        // Fire the loop-complete callback at most once per 200 ms to avoid
        // duplicate calls while the seek is in-flight.
        const now = Date.now();
        if (now - this.lastLoopCompleteTime > 200) {
          this.lastLoopCompleteTime = now;
          if (this.onLoopComplete) {
            this.onLoopComplete();
          }
        }
      }
    }, 100); // poll every 100 ms
  }

  private stopLoopMonitor(): void {
    if (this.loopInterval) {
      clearInterval(this.loopInterval);
      this.loopInterval = null;
    }
  }
}
